import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import { uploadOneReportPhoto } from "../utils/uploadReportPhotos";
import {
  PHOTO_UPLOAD_CONCURRENCY,
  createPhotoJobs,
  retryAllFailedJobs,
  retryJob,
  runPhotoUploadWorkers,
  summarizePhotoJobs,
} from "../utils/photoUploadQueue";

const PhotoUploadContext = createContext(null);

export function PhotoUploadProvider({ children }) {
  const [jobs, setJobsState] = useState([]);
  const jobsRef = useRef([]);
  const pumpingRef = useRef(false);

  const setJobs = useCallback((next) => {
    jobsRef.current = next;
    setJobsState(next);
  }, []);

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    try {
      await runPhotoUploadWorkers({
        getJobs: () => jobsRef.current,
        setJobs,
        concurrency: PHOTO_UPLOAD_CONCURRENCY,
        uploadOne: uploadOneReportPhoto,
        captureException: (error, job) =>
          Sentry.captureException(error, {
            extra: {
              reportId: job.reportId,
              fileName: job.name,
              step: "uploadReportPhoto",
            },
          }),
      });
    } finally {
      pumpingRef.current = false;
      if (jobsRef.current.some((job) => job.status === "waiting")) {
        pump();
      }
    }
  }, [setJobs]);

  const enqueuePhotos = useCallback(
    (reportId, files) => {
      const added = createPhotoJobs(reportId, files);
      if (added.length === 0) return;
      setJobs([...jobsRef.current, ...added]);
      pump();
    },
    [pump, setJobs]
  );

  const retryPhoto = useCallback(
    (jobId) => {
      setJobs(retryJob(jobsRef.current, jobId));
      pump();
    },
    [pump, setJobs]
  );

  const retryFailedPhotos = useCallback(() => {
    setJobs(retryAllFailedJobs(jobsRef.current));
    pump();
  }, [pump, setJobs]);

  const dismissCompleted = useCallback(() => {
    setJobs(jobsRef.current.filter((job) => job.status !== "ready"));
  }, [setJobs]);

  const summary = useMemo(() => summarizePhotoJobs(jobs), [jobs]);

  useEffect(() => {
    if (summary.active === 0) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [summary.active]);

  useEffect(() => {
    if (summary.total === 0 || summary.ready !== summary.total) return undefined;
    const timer = setTimeout(() => setJobs([]), 3500);
    return () => clearTimeout(timer);
  }, [summary.ready, summary.total, setJobs]);

  const value = useMemo(
    () => ({
      jobs,
      summary,
      enqueuePhotos,
      retryPhoto,
      retryFailedPhotos,
      dismissCompleted,
    }),
    [jobs, summary, enqueuePhotos, retryPhoto, retryFailedPhotos, dismissCompleted]
  );

  return (
    <PhotoUploadContext.Provider value={value}>{children}</PhotoUploadContext.Provider>
  );
}

export function usePhotoUpload() {
  const ctx = useContext(PhotoUploadContext);
  if (!ctx) {
    throw new Error("usePhotoUpload must be used within PhotoUploadProvider");
  }
  return ctx;
}
